/**
 * ============================================================================
 * Environment feature flags.
 * ============================================================================
 *
 * Read from Vite env variables, so each deployment decides by its env file — no code change and no
 * separate branch per environment. Set in `.env.production` (prod), or overridden per server in a
 * gitignored `.env.local` / `.env.production.local`.
 *
 * ⚠️ EVERY FLAG DEFAULTS TO THE CURRENT BEHAVIOUR. An environment that sets nothing keeps working
 * exactly as before, so a missing line can never hide a feature by accident.
 *
 * Functions, not constants, so a test can `vi.stubEnv` a flag and see it.
 */

function flag(value: string | undefined): boolean {
  return ['true', '1', 'yes', 'on'].includes((value ?? '').trim().toLowerCase());
}

/**
 * `VITE_SSO_ONLY=true` — sign-in is Microsoft SSO only. Asked for on production.
 *
 * When on:
 *   - the login page offers only "Sign in with Microsoft" (no email / password form);
 *   - Forgot Password is gone, and its route sends the visitor back to /login;
 *   - My Profile has no Change Password section;
 *   - Manage Users has no Reset Password (key) action;
 *   - Create User asks for no password. The backend still REQUIRES one on `POST /management/create`,
 *     so a random one nobody knows is generated and sent — see `generateUnusablePassword`.
 */
export function isSsoOnly(): boolean {
  return flag(import.meta.env.VITE_SSO_ONLY);
}

/**
 * Hides "Bulk upload" tags and the Source filter deployment-wide, overriding `bulk:view_tag`.
 * Unset leaves visibility to that permission, so a missing env line cannot hide the feature.
 */
export function isBulkUploadTagHidden(): boolean {
  return flag(import.meta.env.VITE_HIDE_BULK_UPLOAD_TAG);
}
